// Outils partagés d'impression de l'étiquette colis du dépôt (bordereau
// Chronopost recadré et ajusté au format réel de l'étiquette physique).
// Utilisé par l'onglet « Étiquettes » (DepotLabelPage) et par le bouton
// « Imprimer l'étiquette » du menu ACTIONS d'une commande.

export const PDFJS_SRC = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
export const PDFJS_WORKER_SRC = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

// Zone du bordereau qui contient l'étiquette à imprimer (fractions 0..1).
export const DEFAULT_CROP = { x0: 0.6067, y0: 0.1525, x1: 0.9812, y1: 0.8439 };

// Format réel de l'étiquette autocollante utilisée en boutique : 101 x 150 mm.
export const LABEL_WIDTH_MM = 101;
export const LABEL_HEIGHT_MM = 150;

export function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const script = document.createElement("script");
    script.src = src;
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

// Envoie l'image au format d'étiquette : impression automatique dans
// l'application SogoGestion Desktop, sinon fenêtre d'impression du navigateur.
// Renvoie { ok, error? }.
export async function printLabelImage(dataUrl) {
  if (window.SogoDesktop?.printImage) {
    return (await window.SogoDesktop.printImage(dataUrl, LABEL_WIDTH_MM, LABEL_HEIGHT_MM)) || { ok: false, error: "Échec de l'impression" };
  }
  const win = window.open("", "_blank");
  if (!win) return { ok: false, error: "Fenêtre d'impression bloquée par le navigateur" };
  win.document.write(`<html><head><title>Étiquette</title><style>
    @page { size: ${LABEL_WIDTH_MM}mm ${LABEL_HEIGHT_MM}mm; margin: 0; }
    html, body { margin: 0; padding: 0; }
    .label { width: ${LABEL_WIDTH_MM}mm; height: ${LABEL_HEIGHT_MM}mm; display: flex; align-items: center; justify-content: center; overflow: hidden; }
    .label img { max-width: 100%; max-height: 100%; display: block; }
  </style></head><body><div class="label"><img src="${dataUrl}" onload="window.print();"/></div></body></html>`);
  win.document.close();
  return { ok: true };
}

// ---- Rendu net pour imprimante thermique 203 dpi (8 points/mm) ----
// Le bordereau est rendu directement à la résolution de l'imprimante (203 dpi)
// à la taille physique finale de l'étiquette, puis posé SANS rééchantillonnage
// sur une feuille blanche de 101 x 150 mm (807 x 1199 points) et converti en
// noir et blanc pur : les textes restent lisibles, comme les codes-barres.
export const PRINT_DPI = 203;
export const LABEL_PX_W = Math.round((LABEL_WIDTH_MM / 25.4) * PRINT_DPI);   // 807
export const LABEL_PX_H = Math.round((LABEL_HEIGHT_MM / 25.4) * PRINT_DPI);  // 1199
const BW_THRESHOLD = 185; // luminance < seuil -> noir (légèrement biaisé vers le noir pour épaissir les textes fins)

function toPureBlackAndWhite(canvas) {
  const ctx = canvas.getContext("2d");
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
    const v = d[i + 3] < 32 || lum >= BW_THRESHOLD ? 255 : 0; // transparent = blanc
    d[i] = d[i + 1] = d[i + 2] = v;
    d[i + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

function newLabelSheet() {
  const sheet = document.createElement("canvas");
  sheet.width = LABEL_PX_W;
  sheet.height = LABEL_PX_H;
  const ctx = sheet.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, sheet.width, sheet.height);
  return sheet;
}

// Étiquette 101 x 150 mm à 203 dpi, à partir d'une page PDF pdf.js et d'une
// zone de recadrage (fractions 0..1 de la page).
export async function composeLabelFromPdfPage(page, crop = DEFAULT_CROP) {
  const base = page.getViewport({ scale: 1 }); // dimensions en points (1/72 pouce)
  const cropWmm = ((crop.x1 - crop.x0) * base.width * 25.4) / 72;
  const cropHmm = ((crop.y1 - crop.y0) * base.height * 25.4) / 72;
  const fit = Math.min(LABEL_WIDTH_MM / cropWmm, LABEL_HEIGHT_MM / cropHmm);
  const scale = (PRINT_DPI / 72) * fit; // pixels par point, pour que la zone fasse sa taille finale à 203 dpi
  const viewport = page.getViewport({ scale });
  const source = document.createElement("canvas");
  source.width = Math.ceil(viewport.width);
  source.height = Math.ceil(viewport.height);
  const sctx = source.getContext("2d");
  sctx.fillStyle = "#fff";
  sctx.fillRect(0, 0, source.width, source.height);
  await page.render({ canvasContext: sctx, viewport }).promise;

  const sx = Math.round(crop.x0 * source.width), sy = Math.round(crop.y0 * source.height);
  const cw = Math.min(LABEL_PX_W, Math.round((crop.x1 - crop.x0) * source.width));
  const ch = Math.min(LABEL_PX_H, Math.round((crop.y1 - crop.y0) * source.height));
  const sheet = newLabelSheet();
  sheet.getContext("2d").drawImage(
    source, sx, sy, cw, ch,
    Math.round((LABEL_PX_W - cw) / 2), Math.round((LABEL_PX_H - ch) / 2), cw, ch, // 1:1, sans mise à l'échelle
  );
  return toPureBlackAndWhite(sheet);
}

// Même mise en page à partir d'une image déjà recadrée (étiquette importée en PNG/JPG).
export function composeLabelFromCanvas(cropped) {
  const fit = Math.min(LABEL_PX_W / cropped.width, LABEL_PX_H / cropped.height);
  const w = Math.round(cropped.width * fit), h = Math.round(cropped.height * fit);
  const sheet = newLabelSheet();
  const ctx = sheet.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(cropped, Math.round((LABEL_PX_W - w) / 2), Math.round((LABEL_PX_H - h) / 2), w, h);
  return toPureBlackAndWhite(sheet);
}

// Télécharge le PDF du bordereau lié à la commande, en extrait la zone de
// l'étiquette (même recadrage que l'onglet Étiquettes), la rend à 203 dpi au
// format 101 x 150 mm et l'imprime.
export async function printLabelFromPdfUrl(pdfUrl) {
  await loadScript(PDFJS_SRC);
  if (!window.pdfjsLib) throw new Error("Lecteur PDF indisponible");
  window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_SRC;
  const resp = await fetch(pdfUrl);
  if (!resp.ok) throw new Error("Étiquette introuvable");
  const pdf = await window.pdfjsLib.getDocument({ data: await resp.arrayBuffer() }).promise;
  const page = await pdf.getPage(1);
  const sheet = await composeLabelFromPdfPage(page, DEFAULT_CROP);
  return printLabelImage(sheet.toDataURL("image/png"));
}
